import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Modal from '../components/Modal';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DIAS_SEMANA = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];

const TIPO_EVENTO = {
  instalacion:    { color:'#10B981', icon:'🔧', label:'Instalación' },
  mantenimiento:  { color:'#6366F1', icon:'🛠',  label:'Mantenimiento' },
  retiro:         { color:'#EF4444', icon:'📦', label:'Retiro' },
  visita_tecnica: { color:'#F59E0B', icon:'👷', label:'Visita técnica' },
  cobro:          { color:'#00E5FF', icon:'💳', label:'Cobro' },
  reunion:        { color:'#8B5CF6', icon:'🤝', label:'Reunión' },
  otro:           { color:'#6B7280', icon:'📋', label:'Otro' },
};

const ESTADO_EVENTO = {
  programado:   { color:'#F59E0B', label:'Programado' },
  en_camino:    { color:'#6366F1', label:'En camino' },
  completado:   { color:'#10B981', label:'Completado' },
  cancelado:    { color:'#EF4444', label:'Cancelado' },
  reprogramado: { color:'#6B7280', label:'Reprogramado' },
};

const formVacio = { titulo:'', descripcion:'', tipo:'visita_tecnica', cliente_id:'', ticket_id:'', tecnico_id:'', fecha_inicio:'', fecha_fin:'', direccion:'', notas:'' };

export default function Agenda() {
  const { usuario } = useAuth();
  const hoy = new Date();
  const [vistaActual, setVistaActual] = useState('semana'); // 'dia' | 'semana' | 'mes'
  const [fechaBase, setFechaBase] = useState(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()));
  const [eventos, setEventos] = useState([]);
  const [agendaHoy, setAgendaHoy] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [modal, setModal] = useState(false);
  const [modalEvento, setModalEvento] = useState(false);
  const [eventoActivo, setEventoActivo] = useState(null);
  const [form, setForm] = useState(formVacio);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    let desde, hasta;
    if (vistaActual === 'dia') {
      desde = new Date(fechaBase); hasta = new Date(fechaBase);
      hasta.setHours(23,59,59);
    } else if (vistaActual === 'semana') {
      const diaSemana = fechaBase.getDay();
      desde = new Date(fechaBase); desde.setDate(fechaBase.getDate() - diaSemana);
      hasta = new Date(desde); hasta.setDate(desde.getDate() + 6); hasta.setHours(23,59,59);
    } else {
      desde = new Date(fechaBase.getFullYear(), fechaBase.getMonth(), 1);
      hasta = new Date(fechaBase.getFullYear(), fechaBase.getMonth() + 1, 0, 23,59,59);
    }
    const params = new URLSearchParams({ desde: desde.toISOString(), hasta: hasta.toISOString() });
    const [resE, resH] = await Promise.all([
      api.get(`/api/agenda?${params}`),
      api.get('/api/agenda/hoy'),
    ]);
    setEventos(resE.data);
    setAgendaHoy(resH.data);
  }, [vistaActual, fechaBase]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    api.get('/api/clientes').then(r => setClientes(r.data));
    api.get('/api/usuarios/tecnicos').then(r => setTecnicos(r.data)).catch(() => {});
    api.get('/api/tickets?estado=abierto').then(r => setTickets(r.data)).catch(() => {});
  }, []);

  const navegar = (dir) => {
    const nueva = new Date(fechaBase);
    if (vistaActual === 'dia') nueva.setDate(nueva.getDate() + dir);
    else if (vistaActual === 'semana') nueva.setDate(nueva.getDate() + (dir * 7));
    else nueva.setMonth(nueva.getMonth() + dir);
    setFechaBase(nueva);
  };

  const abrirNuevo = (fecha = null) => {
    const f = fecha ? fecha.toISOString().slice(0,16) : '';
    setForm({ ...formVacio, fecha_inicio: f, tecnico_id: usuario?.rol === 'tecnico' ? usuario.id : '' });
    setError(''); setModal(true);
  };

  const guardar = async () => {
    if (!form.titulo || !form.fecha_inicio) { setError('Título y fecha son requeridos'); return; }
    setGuardando(true);
    try {
      await api.post('/api/agenda', form);
      setModal(false); cargar();
    } catch (err) { setError(err.response?.data?.error || 'Error al guardar'); }
    finally { setGuardando(false); }
  };

  const verEvento = (ev) => { setEventoActivo(ev); setModalEvento(true); };

  const cambiarEstado = async (estado) => {
    try {
      await api.put(`/api/agenda/${eventoActivo.id}`, { estado });
      setEventoActivo(prev => ({ ...prev, estado }));
      cargar();
    } catch (err) { alert('Error'); }
  };

  const eliminar = async () => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try { await api.delete(`/api/agenda/${eventoActivo.id}`); setModalEvento(false); cargar(); }
    catch (err) { alert('Error'); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Generar días del mes para vista mensual
  const generarDiasMes = () => {
    const año = fechaBase.getFullYear();
    const mes = fechaBase.getMonth();
    const primerDia = new Date(año, mes, 1).getDay();
    const diasEnMes = new Date(año, mes + 1, 0).getDate();
    const dias = [];
    for (let i = 0; i < primerDia; i++) dias.push(null);
    for (let i = 1; i <= diasEnMes; i++) dias.push(new Date(año, mes, i));
    return dias;
  };

  // Días de la semana actual
  const generarDiasSemana = () => {
    const diaSemana = fechaBase.getDay();
    const lunes = new Date(fechaBase); lunes.setDate(fechaBase.getDate() - diaSemana);
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(lunes); d.setDate(lunes.getDate() + i); return d; });
  };

  const eventosDelDia = (dia) => eventos.filter(ev => {
    const fev = new Date(ev.fecha_inicio);
    return fev.getDate() === dia.getDate() && fev.getMonth() === dia.getMonth() && fev.getFullYear() === dia.getFullYear();
  });

  const esHoy = (dia) => dia && dia.toDateString() === hoy.toDateString();

  return (
    <Layout>
      <div style={s.header}>
        <div>
          <h1 style={s.titulo}>Agenda</h1>
          <p style={s.sub}>Programación de visitas, instalaciones y eventos</p>
        </div>
        <button style={s.btnPrimario} onClick={() => abrirNuevo()}>+ Nuevo evento</button>
      </div>

      {/* Agenda del día — para técnicos y vista rápida */}
      {agendaHoy.length > 0 && (
        <div style={s.agendaHoy}>
          <div style={s.agendaHoyTitulo}>📅 Hoy — {hoy.toLocaleDateString('es-CO', { weekday:'long', day:'numeric', month:'long' })}</div>
          <div style={s.agendaHoyLista}>
            {agendaHoy.map(ev => {
              const cfg = TIPO_EVENTO[ev.tipo] || TIPO_EVENTO.otro;
              const eCfg = ESTADO_EVENTO[ev.estado] || ESTADO_EVENTO.programado;
              return (
                <div key={ev.id} style={{ ...s.agendaHoyItem, borderLeft:`3px solid ${cfg.color}` }} onClick={() => verEvento(ev)}>
                  <span style={{ fontSize:'18px' }}>{cfg.icon}</span>
                  <div style={{ flex:1 }}>
                    <div style={s.agendaHoyNombre}>{ev.titulo}</div>
                    {ev.cliente_nombre && <div style={s.agendaHoySub}>👤 {ev.cliente_nombre}</div>}
                    {ev.cliente_dir && <div style={s.agendaHoySub}>📍 {ev.cliente_dir}</div>}
                    {ev.ticket_descripcion && <div style={s.agendaHoySub}>🎫 {ev.ticket_numero}: {ev.ticket_descripcion?.slice(0,60)}</div>}
                  </div>
                  <div style={s.agendaHoyDerecha}>
                    <div style={s.agendaHoyHora}>{new Date(ev.fecha_inicio).toLocaleTimeString('es-CO', { hour:'2-digit', minute:'2-digit' })}</div>
                    <span style={{ fontSize:'11px', color: eCfg.color }}>{eCfg.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Controles del calendario */}
      <div style={s.calControles}>
        <div style={s.vistaBtns}>
          {['dia','semana','mes'].map(v => (
            <button key={v} onClick={() => setVistaActual(v)}
              style={{ ...s.vistaBtn, ...(vistaActual === v ? s.vistaBtnActivo : {}) }}>
              {v === 'dia' ? 'Día' : v === 'semana' ? 'Semana' : 'Mes'}
            </button>
          ))}
        </div>
        <div style={s.navFecha}>
          <button style={s.navBtn} onClick={() => navegar(-1)}>‹</button>
          <span style={s.navLabel}>
            {vistaActual === 'mes' ? `${MESES[fechaBase.getMonth()]} ${fechaBase.getFullYear()}`
              : vistaActual === 'semana' ? `Semana del ${generarDiasSemana()[0].toLocaleDateString('es-CO')}`
              : fechaBase.toLocaleDateString('es-CO', { weekday:'long', day:'numeric', month:'long' })}
          </span>
          <button style={s.navBtn} onClick={() => navegar(1)}>›</button>
          <button style={s.hoyCta} onClick={() => setFechaBase(new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()))}>Hoy</button>
        </div>
      </div>

      {/* Vista semana */}
      {vistaActual === 'semana' && (
        <div style={s.semanaGrid}>
          {generarDiasSemana().map((dia, i) => {
            const evsDia = eventosDelDia(dia);
            return (
              <div key={i} style={{ ...s.semanaCol, ...(esHoy(dia) ? s.semanaColHoy : {}) }}>
                <div style={s.semanaDiaLabel}>
                  <span style={s.semanaDiaNombre}>{DIAS_SEMANA[dia.getDay()]}</span>
                  <span style={{ ...s.semanaDiaNum, ...(esHoy(dia) ? s.semanaDiaNumHoy : {}) }}>{dia.getDate()}</span>
                </div>
                <div style={s.semanaEventos}>
                  {evsDia.map(ev => {
                    const cfg = TIPO_EVENTO[ev.tipo] || TIPO_EVENTO.otro;
                    return (
                      <div key={ev.id} style={{ ...s.semanaEvento, background: `${cfg.color}20`, borderLeft:`3px solid ${cfg.color}` }} onClick={() => verEvento(ev)}>
                        <div style={s.semanaEventoHora}>{new Date(ev.fecha_inicio).toLocaleTimeString('es-CO',{hour:'2-digit',minute:'2-digit'})}</div>
                        <div style={s.semanaEventoTitulo}>{cfg.icon} {ev.titulo}</div>
                        {ev.cliente_nombre && <div style={s.semanaEventoCliente}>{ev.cliente_nombre}</div>}
                      </div>
                    );
                  })}
                  <button style={s.addEvento} onClick={() => abrirNuevo(dia)}>+</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Vista mes */}
      {vistaActual === 'mes' && (
        <div style={s.mesGrid}>
          {DIAS_SEMANA.map(d => <div key={d} style={s.mesDiaNombreHeader}>{d}</div>)}
          {generarDiasMes().map((dia, i) => (
            <div key={i} style={{ ...s.mesCelda, ...(dia && esHoy(dia) ? s.mesCeldaHoy : {}), ...(!dia ? s.mesCeldaVacia : {}) }}>
              {dia && (
                <>
                  <div style={s.mesDiaNum}>{dia.getDate()}</div>
                  {eventosDelDia(dia).slice(0, 3).map(ev => {
                    const cfg = TIPO_EVENTO[ev.tipo] || TIPO_EVENTO.otro;
                    return (
                      <div key={ev.id} style={{ ...s.mesEvento, background: `${cfg.color}25`, color: cfg.color }} onClick={() => verEvento(ev)}>
                        {cfg.icon} {ev.titulo.slice(0,15)}
                      </div>
                    );
                  })}
                  {eventosDelDia(dia).length > 3 && <div style={s.masEventos}>+{eventosDelDia(dia).length - 3} más</div>}
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Vista día */}
      {vistaActual === 'dia' && (
        <div style={s.diaLista}>
          {eventosDelDia(fechaBase).length === 0 ? (
            <div style={s.vacio}>No hay eventos para este día. <button style={s.btnAddDia} onClick={() => abrirNuevo(fechaBase)}>+ Agregar evento</button></div>
          ) : eventosDelDia(fechaBase).map(ev => {
            const cfg = TIPO_EVENTO[ev.tipo] || TIPO_EVENTO.otro;
            const eCfg = ESTADO_EVENTO[ev.estado] || ESTADO_EVENTO.programado;
            return (
              <div key={ev.id} style={{ ...s.diaEvento, borderLeft:`4px solid ${cfg.color}` }} onClick={() => verEvento(ev)}>
                <span style={{ fontSize:'24px' }}>{cfg.icon}</span>
                <div style={{ flex:1 }}>
                  <div style={s.diaEventoTitulo}>{ev.titulo}</div>
                  {ev.cliente_nombre && <div style={s.diaEventoInfo}>👤 {ev.cliente_nombre} · 📞 {ev.cliente_telefono}</div>}
                  {(ev.direccion || ev.cliente_dir) && <div style={s.diaEventoInfo}>📍 {ev.direccion || ev.cliente_dir}</div>}
                  {ev.tecnico_nombre && <div style={s.diaEventoInfo}>🔧 {ev.tecnico_nombre}</div>}
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={s.diaEventoHora}>{new Date(ev.fecha_inicio).toLocaleTimeString('es-CO',{hour:'2-digit',minute:'2-digit'})}</div>
                  <span style={{ fontSize:'12px', color: eCfg.color }}>{eCfg.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal nuevo evento */}
      <Modal abierto={modal} onCerrar={() => setModal(false)} titulo="Nuevo Evento" ancho="580px">
        <div style={s.formGrid}>
          <div style={{ ...s.campo, gridColumn:'1/-1' }}>
            <label style={s.label}>Título *</label>
            <input style={s.input} value={form.titulo} onChange={e => f('titulo',e.target.value)} placeholder="Ej: Instalación cliente nuevo" />
          </div>
          <div style={s.campo}>
            <label style={s.label}>Tipo</label>
            <select style={s.input} value={form.tipo} onChange={e => f('tipo',e.target.value)}>
              {Object.entries(TIPO_EVENTO).map(([k,v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
            </select>
          </div>
          <div style={s.campo}>
            <label style={s.label}>Técnico</label>
            <select style={s.input} value={form.tecnico_id} onChange={e => f('tecnico_id',e.target.value)}>
              <option value="">Sin asignar</option>
              {tecnicos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
            </select>
          </div>
          <div style={s.campo}>
            <label style={s.label}>Fecha y hora inicio *</label>
            <input style={s.input} type="datetime-local" value={form.fecha_inicio} onChange={e => f('fecha_inicio',e.target.value)} />
          </div>
          <div style={s.campo}>
            <label style={s.label}>Fecha y hora fin</label>
            <input style={s.input} type="datetime-local" value={form.fecha_fin} onChange={e => f('fecha_fin',e.target.value)} />
          </div>
          <div style={s.campo}>
            <label style={s.label}>Cliente</label>
            <select style={s.input} value={form.cliente_id} onChange={e => f('cliente_id',e.target.value)}>
              <option value="">Sin cliente</option>
              {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
          <div style={s.campo}>
            <label style={s.label}>Ticket relacionado</label>
            <select style={s.input} value={form.ticket_id} onChange={e => f('ticket_id',e.target.value)}>
              <option value="">Sin ticket</option>
              {tickets.map(t => <option key={t.id} value={t.id}>{t.numero} — {t.titulo?.slice(0,30)}</option>)}
            </select>
          </div>
          <div style={{ ...s.campo, gridColumn:'1/-1' }}>
            <label style={s.label}>Dirección</label>
            <input style={s.input} value={form.direccion} onChange={e => f('direccion',e.target.value)} placeholder="Dirección de la visita" />
          </div>
          <div style={{ ...s.campo, gridColumn:'1/-1' }}>
            <label style={s.label}>Notas</label>
            <textarea style={{ ...s.input, minHeight:'60px', resize:'vertical' }} value={form.notas} onChange={e => f('notas',e.target.value)} placeholder="Instrucciones, observaciones..." />
          </div>
        </div>
        {error && <div style={s.error}>{error}</div>}
        <div style={s.modalFooter}>
          <button style={s.btnSec} onClick={() => setModal(false)}>Cancelar</button>
          <button style={s.btnPrimario} onClick={guardar} disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar evento'}</button>
        </div>
      </Modal>

      {/* Modal detalle evento */}
      {eventoActivo && (
        <Modal abierto={modalEvento} onCerrar={() => setModalEvento(false)} titulo={eventoActivo.titulo}>
          {(() => {
            const cfg = TIPO_EVENTO[eventoActivo.tipo] || TIPO_EVENTO.otro;
            const eCfg = ESTADO_EVENTO[eventoActivo.estado] || ESTADO_EVENTO.programado;
            return (
              <>
                <div style={s.eventoDetalleBadges}>
                  <span style={{ fontSize:'28px' }}>{cfg.icon}</span>
                  <span style={{ ...s.badge2, color: cfg.color }}>{cfg.label}</span>
                  <span style={{ ...s.badge2, color: eCfg.color }}>{eCfg.label}</span>
                </div>
                <div style={s.eventoDetalleInfo}>
                  <div style={s.eventoDetalleItem}>⏰ {new Date(eventoActivo.fecha_inicio).toLocaleString('es-CO')}{eventoActivo.fecha_fin ? ` → ${new Date(eventoActivo.fecha_fin).toLocaleTimeString('es-CO',{hour:'2-digit',minute:'2-digit'})}` : ''}</div>
                  {eventoActivo.cliente_nombre && <div style={s.eventoDetalleItem}>👤 {eventoActivo.cliente_nombre} · 📞 {eventoActivo.cliente_telefono}</div>}
                  {eventoActivo.direccion && <div style={s.eventoDetalleItem}>📍 {eventoActivo.direccion}</div>}
                  {eventoActivo.tecnico_nombre && <div style={s.eventoDetalleItem}>🔧 {eventoActivo.tecnico_nombre}</div>}
                  {eventoActivo.ticket_numero && <div style={s.eventoDetalleItem}>🎫 Ticket: {eventoActivo.ticket_numero}</div>}
                  {eventoActivo.notas && <div style={s.eventoDetalleItem}>📝 {eventoActivo.notas}</div>}
                </div>
                {/* Cambiar estado */}
                {eventoActivo.estado !== 'completado' && eventoActivo.estado !== 'cancelado' && (
                  <div style={s.estadoBtns}>
                    {eventoActivo.estado === 'programado' && <button style={s.btnEstado} onClick={() => cambiarEstado('en_camino')}>🚗 En camino</button>}
                    {(eventoActivo.estado === 'en_camino' || eventoActivo.estado === 'programado') && (
                      <button style={{ ...s.btnEstado, background:'rgba(16,185,129,0.15)', color:'#6EE7B7' }} onClick={() => cambiarEstado('completado')}>✅ Completado</button>
                    )}
                    <button style={{ ...s.btnEstado, background:'rgba(245,158,11,0.1)', color:'#FCD34D' }} onClick={() => cambiarEstado('reprogramado')}>🔄 Reprogramar</button>
                  </div>
                )}
                <div style={s.modalFooter}>
                  <button style={s.btnDel} onClick={eliminar}>Eliminar</button>
                  <button style={s.btnSec} onClick={() => setModalEvento(false)}>Cerrar</button>
                </div>
              </>
            );
          })()}
        </Modal>
      )}
    </Layout>
  );
}

const s = {
  header: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom:'20px' },
  titulo: { fontSize:'24px', fontWeight:'700', marginBottom:'4px' },
  sub: { color:'rgba(255,255,255,0.4)', fontSize:'14px' },
  btnPrimario: { background:'linear-gradient(135deg,#00E5FF,#6366F1)', border:'none', borderRadius:'10px', padding:'10px 20px', color:'#fff', fontSize:'14px', fontWeight:'600', cursor:'pointer' },
  btnSec: { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'10px', padding:'10px 18px', color:'#fff', fontSize:'14px', cursor:'pointer' },
  btnDel: { background:'rgba(239,68,68,0.1)', border:'none', borderRadius:'8px', padding:'8px 14px', color:'#FCA5A5', fontSize:'13px', cursor:'pointer' },
  agendaHoy: { background:'rgba(0,229,255,0.04)', border:'1px solid rgba(0,229,255,0.15)', borderRadius:'14px', padding:'16px', marginBottom:'20px' },
  agendaHoyTitulo: { fontSize:'13px', fontWeight:'600', color:'#00E5FF', marginBottom:'12px' },
  agendaHoyLista: { display:'flex', flexDirection:'column', gap:'8px' },
  agendaHoyItem: { display:'flex', alignItems:'flex-start', gap:'12px', background:'rgba(255,255,255,0.03)', borderRadius:'8px', padding:'10px 14px', cursor:'pointer' },
  agendaHoyNombre: { fontSize:'13px', fontWeight:'600', marginBottom:'2px' },
  agendaHoySub: { fontSize:'11px', color:'rgba(255,255,255,0.5)', lineHeight:'1.6' },
  agendaHoyDerecha: { textAlign:'right', minWidth:'70px' },
  agendaHoyHora: { fontSize:'14px', fontWeight:'700', color:'#fff' },
  calControles: { display:'flex', flexWrap:'wrap', gap:'10px', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' },
  vistaBtns: { display:'flex', gap:'4px' },
  vistaBtn: { background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'8px', padding:'6px 14px', color:'rgba(255,255,255,0.5)', fontSize:'13px', cursor:'pointer' },
  vistaBtnActivo: { background:'rgba(0,229,255,0.1)', border:'1px solid rgba(0,229,255,0.3)', color:'#00E5FF' },
  navFecha: { display:'flex', flexWrap:'wrap', alignItems:'center', gap:'10px' },
  navBtn: { background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'8px', width:'32px', height:'32px', color:'#fff', fontSize:'18px', cursor:'pointer' },
  navLabel: { fontSize:'14px', fontWeight:'600', minWidth:'min(200px,40vw)', textAlign:'center' },
  hoyCta: { background:'rgba(0,229,255,0.1)', border:'1px solid rgba(0,229,255,0.3)', borderRadius:'8px', padding:'5px 12px', color:'#00E5FF', fontSize:'12px', cursor:'pointer' },
  semanaGrid: { display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'8px' },
  semanaCol: { background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'10px', padding:'8px', minHeight:'120px' },
  semanaColHoy: { background:'rgba(0,229,255,0.04)', border:'1px solid rgba(0,229,255,0.2)' },
  semanaDiaLabel: { display:'flex', flexDirection:'column', alignItems:'center', marginBottom:'8px' },
  semanaDiaNombre: { fontSize:'10px', color:'rgba(255,255,255,0.4)', textTransform:'uppercase' },
  semanaDiaNum: { fontSize:'18px', fontWeight:'700', lineHeight:'1.2' },
  semanaDiaNumHoy: { color:'#00E5FF' },
  semanaEventos: { display:'flex', flexDirection:'column', gap:'4px' },
  semanaEvento: { borderRadius:'6px', padding:'4px 6px', cursor:'pointer', fontSize:'11px' },
  semanaEventoHora: { color:'rgba(255,255,255,0.5)', fontSize:'10px' },
  semanaEventoTitulo: { fontWeight:'600', fontSize:'11px' },
  semanaEventoCliente: { fontSize:'10px', color:'rgba(255,255,255,0.4)' },
  addEvento: { background:'none', border:'1px dashed rgba(255,255,255,0.1)', borderRadius:'6px', width:'100%', padding:'3px', color:'rgba(255,255,255,0.2)', fontSize:'16px', cursor:'pointer', marginTop:'4px' },
  mesGrid: { display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:'4px' },
  mesDiaNombreHeader: { textAlign:'center', fontSize:'11px', color:'rgba(255,255,255,0.4)', padding:'8px 0', textTransform:'uppercase' },
  mesCelda: { background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'8px', padding:'6px', minHeight:'80px' },
  mesCeldaHoy: { background:'rgba(0,229,255,0.06)', border:'1px solid rgba(0,229,255,0.2)' },
  mesCeldaVacia: { background:'transparent', border:'none' },
  mesDiaNum: { fontSize:'13px', fontWeight:'600', marginBottom:'4px' },
  mesEvento: { fontSize:'10px', fontWeight:'600', padding:'2px 5px', borderRadius:'4px', marginBottom:'2px', cursor:'pointer', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' },
  masEventos: { fontSize:'10px', color:'rgba(255,255,255,0.4)' },
  diaLista: { display:'flex', flexDirection:'column', gap:'10px' },
  vacio: { padding:'60px', textAlign:'center', color:'rgba(255,255,255,0.3)', fontSize:'14px' },
  diaEvento: { display:'flex', gap:'14px', background:'rgba(255,255,255,0.04)', borderRadius:'12px', padding:'16px', cursor:'pointer' },
  diaEventoTitulo: { fontSize:'15px', fontWeight:'600', marginBottom:'6px' },
  diaEventoInfo: { fontSize:'12px', color:'rgba(255,255,255,0.5)', lineHeight:'1.8' },
  diaEventoHora: { fontSize:'16px', fontWeight:'700', marginBottom:'4px' },
  btnAddDia: { background:'none', border:'none', color:'#00E5FF', cursor:'pointer', textDecoration:'underline', fontSize:'14px' },
  eventoDetalleBadges: { display:'flex', alignItems:'center', gap:'10px', marginBottom:'14px' },
  badge2: { fontSize:'12px', fontWeight:'600', background:'rgba(255,255,255,0.06)', padding:'4px 12px', borderRadius:'20px' },
  eventoDetalleInfo: { display:'flex', flexDirection:'column', gap:'8px', marginBottom:'16px' },
  eventoDetalleItem: { fontSize:'13px', color:'rgba(255,255,255,0.7)', lineHeight:'1.6' },
  estadoBtns: { display:'flex', gap:'8px', marginBottom:'16px', flexWrap:'wrap' },
  btnEstado: { background:'rgba(99,102,241,0.15)', border:'none', borderRadius:'8px', padding:'8px 14px', color:'#A5B4FC', fontSize:'13px', cursor:'pointer', fontWeight:'600' },
  formGrid: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'16px' },
  campo: { display:'flex', flexDirection:'column', gap:'6px' },
  label: { color:'rgba(255,255,255,0.6)', fontSize:'13px' },
  input: { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', padding:'10px 14px', color:'#fff', fontSize:'14px', outline:'none' },
  error: { background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'8px', padding:'10px', color:'#FCA5A5', fontSize:'13px', marginBottom:'16px' },
  modalFooter: { display:'flex', gap:'12px', justifyContent:'flex-end' },
};
